export interface ILensColumnReference {
  columnName: string;
}

/**
 * Refers to the column named `columnName` on the dimension specified by `dimension.id`
 */
export interface IDimensionColumn {
  dimension: IRelation;
  columnName: string;
}

/**
 * Refers to the column named `columnName` on the dimension specified by `dimension.id`.
 *
 * The `joinColumnName` property is the lens column that can be joined against the dimension's "joinable" column
 */
export interface IDimensionColumnReference extends IDimensionColumn {
  joinColumnName: string;
}

export interface IRelation {
  id: string;
}

export interface IStringConstantExpression {
  constant: string;
}

export interface MultiHierarchyGroupReference {
  segmentId: string;
  groupId: string;
}

/**
 * "Organize columns by Account Number" reference for multi-hierarchy reports.
 *
 * Like a {@link MultiHierarchyGroupReference} it scopes the accounts via
 * `segmentId`/`groupId`, but the `accountNumber` marker tells the columns
 * pipeline to enumerate leaf `account_number_id` values (headed by the
 * account-number string) instead of the segment's `child_group_id`.
 */
export interface MultiHierarchyAccountNumberReference {
  segmentId: string;
  groupId: string;
  accountNumber: true;
}

export type IProjection =
  | ILensColumnReference
  | IDimensionColumnReference
  | IStringConstantExpression
  | MultiHierarchyGroupReference
  | MultiHierarchyAccountNumberReference;

export type IColumnGroupColumn = ILensColumnReference | IDimensionColumnReference;

export type FilterableColumnTypes =
  | IDimensionColumn
  | ILensColumnReference
  | IDimensionColumnReference
  | IStringConstantExpression
  | MultiHierarchyGroupReference
  | MultiHierarchyAccountNumberReference;

export const isMultiHierarchyAccountNumberColumnReference = (
  projection: IProjection
): projection is MultiHierarchyAccountNumberReference =>
  (projection as MultiHierarchyAccountNumberReference)?.accountNumber === true &&
  (projection as MultiHierarchyAccountNumberReference)?.groupId !== undefined;

export const isMultiHierarchyGroupReference = (projection: IProjection): projection is MultiHierarchyGroupReference =>
  (projection as MultiHierarchyGroupReference)?.groupId !== undefined &&
  (projection as MultiHierarchyAccountNumberReference)?.accountNumber !== true;

/**
 * A segment/group-scoped column reference — either a plain segment "organize by" reference or
 * an account-number one. Both carry `segmentId`/`groupId`, so callers that only need the scope
 * (CoA validation, column-value-mapping keys) should use this instead of narrowing to just one.
 */
export const isSegmentScopedColumnReference = (
  projection: IProjection
): projection is MultiHierarchyGroupReference | MultiHierarchyAccountNumberReference =>
  isMultiHierarchyGroupReference(projection) || isMultiHierarchyAccountNumberColumnReference(projection);
